/**
 * Utilities for working with Content Security Policy nonces
 *
 * The CSP nonce is injected into the HTML by the Vite CSP plugin
 * and can be accessed via a meta tag for runtime use.
 */

/**
 * Get the current CSP nonce from the meta tag
 * This can be used to add nonce attributes to dynamically created elements
 *
 * @returns The CSP nonce or undefined if not found
 */
export function getCspNonce(): string | undefined {
  const metaTag = document.querySelector('meta[name="csp-nonce"]');
  return metaTag?.getAttribute('content') || undefined;
}

/**
 * Create a script element with the CSP nonce
 * Use this when dynamically creating script elements
 *
 * @param source - The source URL for the script
 * @returns A script element with the nonce attribute set
 */
export function createScriptWithNonce(source: string): HTMLScriptElement {
  const script = document.createElement('script');
  script.src = source;

  const nonce = getCspNonce();
  if (nonce) {
    script.setAttribute('nonce', nonce);
  }

  return script;
}

/**
 * Create a style element with the CSP nonce
 * Use this when dynamically creating style elements
 *
 * @param cssText - The CSS text content
 * @returns A style element with the nonce attribute set
 */
export function createStyleWithNonce(cssText: string): HTMLStyleElement {
  const style = document.createElement('style');
  style.textContent = cssText;

  const nonce = getCspNonce();
  if (nonce) {
    style.setAttribute('nonce', nonce);
  }

  return style;
}
